import React, { useState, useEffect, ReactElement, CSSProperties } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { Dispatch } from 'redux';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../Common/Notification';
import { TASK_LIST_MENU_ITEMS } from '../../constants/constants';
import * as types from '../../constants/actionTypes';
import { actions as locationTaskUserFilterActions } from '../../actions/locationTaskUserFilterActions';
import { actions as locationTaskActions } from '../../actions/locationTaskActions';
import './ManageTaskViews.scss';

interface LocationTaskUserFilters {
	id: string;
	filters: Array<UserFilter>;
}

interface UserFilter {
	filterName: string;
	ordinal: number;
	isCustom: boolean;
	isDefault: boolean;
}

const ManageTaskViews = (): ReactElement => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [userFilters, setUserFilters] = useState<LocationTaskUserFilters>({ id: '', filters: [] });

	const dispatch: Dispatch = useDispatch();
	const getFilters = async () => dispatch(locationTaskUserFilterActions.listUserFilters());
	const saveFilters = async (filters: LocationTaskUserFilters) => dispatch(locationTaskUserFilterActions.saveUserFilters(filters));
	const deleteFilter = async (filterName: string) => dispatch(locationTaskActions.deleteCustomFilter(filterName));
	const setTasksPageTaskMenuItems = async (items: Array<any>) => dispatch(locationTaskActions.setTasksPageTaskMenuItems(items));
	const setTasksMenuSelected = async (selectedValue: string) => dispatch(locationTaskActions.setTasksMenuSelected(selectedValue));

	const history = useHistory();
	const { tasksMenuSelectedValue } = useSelector((state: any) => state.taskListState);

	useEffect(() => {
		document.title = 'Manage Task Views';
		
		loadFilters();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const loadFilters = async () => {
		setIsLoading(true);
		try {
			const response: any = await getFilters();
			if (response.type === types.LIST_LOCATION_TASK_USER_FILTERS_SUCCESS) {
				setUserFilters(response.payload);
			} else if (response.type === types.LIST_LOCATION_TASK_USER_FILTERS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}
	};

	const onDragEnd = async (result: any) => {
		// If dropped outside any droppable area or no valid destination, do nothing
		if (!result.destination) {
			return;
		}

		const updatedFilters = [...userFilters.filters];
		// Remove the dragged filter from its original position
		const [removed] = updatedFilters.splice(result.source.index, 1);

		// Insert it in the new position
		updatedFilters.splice(result.destination.index, 0, removed);

		// Reassign ordinals
		updatedFilters.forEach((f, idx) => {
			f.ordinal = idx + 1;
		});

		const newFilters: LocationTaskUserFilters = {
			id: userFilters.id,
			filters: updatedFilters,
		};

		setIsLoading(true);
		try {
			const response: any = await saveFilters(newFilters);
			if (response.type === types.SAVE_LOCATION_TASK_USER_FILTERS_SUCCESS) {
				newFilters.id = response.payload.id;
				Notification('success', '', 'Filters saved');
			} else if (response.type === types.SAVE_LOCATION_TASK_USER_FILTERS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}

		setUserFilters(newFilters);
	};

	const getListStyle = (isDraggingOver: boolean) => ({
		background: isDraggingOver ? '#eee' : 'white',
		borderRadius: '0.25rem',
	});

	const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
		userSelect: 'none',
		lineHeight: 1,
		margin: '0 0 2px 0',
		background: 'white',
		border: isDragging ? '2px solid #03a9f4' : '1px solid #DEE2E6',
		...draggableStyle,
	});

	const trashIconStyle = (filter: UserFilter): CSSProperties => {
		return {
			visibility: filter.isCustom ? 'visible' : 'hidden',
			cursor: 'pointer',
		};
	};

	const onDeleteFilter = async (ordinal: number) => {
		const foundFilter = userFilters.filters.find((f) => f.ordinal === ordinal);
		if (!foundFilter) return;

		const filterName = foundFilter.filterName;
		// Remove from the list
		const updated = userFilters.filters.filter((f) => f.ordinal !== ordinal);
		// Re-ordinal
		updated.forEach((f, idx) => {
			f.ordinal = idx + 1;
		});

		const newFilters: LocationTaskUserFilters = { id: userFilters.id, filters: updated };

		setIsLoading(true);
		try {
			// Save the new list
			const saveResponse: any = await saveFilters(newFilters);
			if (saveResponse.type === types.SAVE_LOCATION_TASK_USER_FILTERS_SUCCESS) {
				// Then delete the filter definition from the backend
				const deleteResponse: any = await deleteFilter(filterName);
				if (deleteResponse.type === types.DELETE_FILTER_SUCCESS) {
					Notification('success', '', 'Filter deleted');
				} else if (deleteResponse.type === types.DELETE_FILTER_FAILURE) {
					Notification('error', 'Error', deleteResponse.payload.message);
				}
			} else if (saveResponse.type === types.SAVE_LOCATION_TASK_USER_FILTERS_FAILURE) {
				Notification('error', 'Error', saveResponse.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}

		setUserFilters(newFilters);

		// Update the task page filter items in Redux
		const menuItems = updated.map((f) => ({ filterName: f.filterName, isCustom: f.isCustom }));
		await setTasksPageTaskMenuItems(menuItems || []);

		// If the deleted filter was currently selected, reset to default
		if (filterName === tasksMenuSelectedValue) {
			await setTasksMenuSelected('');
		}
	};

	const FilterItem = ({ filter, index }: { filter: UserFilter; index: number }) => {
		if (filter.isDefault) {
			// The default filter is not re-orderable
			return (
				<li className='list-group-item d-flex justify-content-between' style={getItemStyle(false, {})}>
					<i className='fas fa-bars me-4' style={{ visibility: 'hidden' }}></i>
					<span className='flex-grow-1'>{filter.filterName}</span>
					<i className='far fa-trash-can ms-2' style={trashIconStyle(filter)}></i>
				</li>
			);
		}

		return (
			<Draggable key={filter.ordinal} draggableId={String(filter.ordinal)} index={index}>
				{(provided, snapshot) => (
					<li
						className='list-group-item d-flex justify-content-between'
						ref={provided.innerRef}
						{...provided.draggableProps}
						{...provided.dragHandleProps}
						style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}>
						<i className='fas fa-bars me-4'></i>
						<span className='flex-grow-1'>{filter.filterName}</span>
						<i className='far fa-trash-can ms-2' style={trashIconStyle(filter)} onClick={() => onDeleteFilter(filter.ordinal)}></i>
					</li>
				)}
			</Draggable>
		);
	};

	return (
		<div className='manage-task-views'>
			<LoadingIndicator isLoading={isLoading} isFullscreen>
				<div>
					<div className='mtv-breadcrumb row no-gutters'>
						<div className='col-12'>
							<a onClick={() => history.goBack()}>
								<i className='far fa-chevron-left'></i>
								<span>Go Back</span>
							</a>
						</div>
					</div>

					<div className='heading-nav row no-gutters'>
						<div className='col'>
							<div className='heading-title'>Manage Task Views</div>
						</div>
					</div>

					<div className='mtv-main row no-gutters'>
						<DragDropContext
							onDragEnd={(result) => {
								onDragEnd(result);
							}}>
							<Droppable droppableId='manageTaskViews'>
								{(provided, snapshot) => (
									<ul
										className='list-group col-sm-8'
										ref={provided.innerRef}
										{...provided.droppableProps}
										style={getListStyle(snapshot.isDraggingOver)}>
										{/* Always render the Draggables, even if userFilters.filters is empty */}
										{userFilters.filters.map((filter, index) => (
											<FilterItem filter={filter} index={index} key={filter.ordinal} />
										))}
										{provided.placeholder}
									</ul>
								)}
							</Droppable>
						</DragDropContext>
					</div>
				</div>
			</LoadingIndicator>
		</div>
	);
};

export default ManageTaskViews;
